import { useLocalStorageState, useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import type { FC, ReactElement } from 'react';
import { cloneElement, useEffect, useMemo, useState } from 'react';
import { ProductLineApply1 } from '@/app/components/icons/ProductLineApply1';
import { BackfillTargetOption } from '@/app/components/shared/BackfillTargetOption';
import {
  HoverCard,
  HoverCardArrow,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@/app/components/ui/hover-card';
import { toast } from '@/app/components/ui/use-toast';
import { KEEP_CONTINUE_CREATE_PROMPT_STORAGE_KEY } from '@/app/constants';
import { useBackfill, useDevice, useFetch, useGlobalModal } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { CreationProps } from '@/app/types';
import { BackfillTaskType } from '@/app/types';
import { appendUnMentionedReferences, cn, getImageSize, parseTextToTextPromptWithReference } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { inspirationsGetInspirationDetail } from './apis';
import type { InspirationsGetInspirationDetailError, InspirationsGetInspirationDetailResponse } from './types';

const BackfillTaskType2String: Partial<Record<BackfillTaskType, string>> = {
  [BackfillTaskType.CHARACTER2VIDEO]: 'reference2video',
  [BackfillTaskType.IMG2VIDEO]: 'image2video',
  [BackfillTaskType.REFERENCE2IMAGE]: 'reference2image',
  [BackfillTaskType.STYLE_TRANSFER]: 'styleTransfer',
  [BackfillTaskType.STORY_GRID]: 'storyGrid',
  [BackfillTaskType.MOTION_CONTROL]: 'motionControl',
  [BackfillTaskType.FACE_SWAP]: 'faceSwap',
};

const IMAGE_BACKFILL_TARGETS = [
  {
    labelKey: 'create.tabCharacter2Video',
    key: BackfillTaskType.CHARACTER2VIDEO,
    path: '/create/character2video',
  },
  {
    labelKey: 'create.tabImg2Video',
    key: BackfillTaskType.IMG2VIDEO,
    path: '/create/img2video',
  },
  {
    labelKey: 'create.tabReference2Image',
    key: BackfillTaskType.REFERENCE2IMAGE,
    path: '/create/reference2image',
  },
  {
    labelKey: 'create.tabStyleTransfer',
    key: BackfillTaskType.STYLE_TRANSFER,
    path: '/create/style-transfer',
  },
  {
    labelKey: 'tools.storyGrid.title',
    key: BackfillTaskType.STORY_GRID,
    path: '/create/story-grid',
  },
  {
    labelKey: 'tools.motionControl.title',
    key: BackfillTaskType.MOTION_CONTROL,
    path: '/create/motion-control',
  },
  {
    labelKey: 'create.tabFaceSwap',
    key: BackfillTaskType.FACE_SWAP,
    path: '/create/face-swap',
  },
] as const;

interface ImageApplyButtonProps {
  className?: string;
  /** 图片URI，可选。如果不提供则会通过inspirationId异步获取 */
  uri?: string;
  /** 无水印图片url，可选。如果不提供则会通过inspirationId异步获取 */
  noMarkUri?: string;
  /** 自定义触发元素，不传则使用默认的 ProductLineApply1 图标 */
  content?: ReactElement;

  /** 灵感ID，当uri不存在时必须提供用于异步获取图片数据 */
  inspirationId?: string;
  /** 直接应用模式，不显示hover选择菜单，直接应用到指定类型 */
  directApplyType?: BackfillTaskType;
  onApplyToTool?: CreationProps['onApplyToTool'];
  container?: HTMLElement | null;
  creationId?: string;
  showFlyTrace?: boolean;
  align?: 'start' | 'center' | 'end';
  /** 应用后是否在新标签页打开目标工具 */
  openInNewTab?: boolean;
  backfillTextPrompt?: string;
  titleText?: string;
}

export const ImageApplyButton: FC<ImageApplyButtonProps> = ({
  className,
  uri,
  noMarkUri,
  onApplyToTool,
  content,
  inspirationId,
  directApplyType,
  container,
  creationId,
  showFlyTrace,
  openInNewTab,
  backfillTextPrompt,
  titleText,
}) => {
  const t = useTranslations();
  const storeApi = useStoreApi();
  const { supportsHover } = useDevice();

  const isAsyncMode = !uri;
  const isDirectMode = !!directApplyType;

  const [selectedType, setSelectedType] = useState<BackfillTaskType>(
    directApplyType || BackfillTaskType.CHARACTER2VIDEO,
  );
  const [keepPrompt = true, setKeepPrompt] = useLocalStorageState<boolean>(KEEP_CONTINUE_CREATE_PROMPT_STORAGE_KEY, {
    defaultValue: true,
    listenStorageChange: true,
  });
  const [isNonHoverCardOpen, setIsNonHoverCardOpen] = useState(false);
  const pathname = usePathname();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { getBackfillAvailable, handleBackfillImageByUrl } = useBackfill({ openInNewTab });
  const { setFlyPending, setFormParams, updateStyleTransferFormParams } = useStore((state) => ({
    setFlyPending: state.setFlyPending,
    setFormParams: state.setFormParams,
    updateStyleTransferFormParams: state.updateStyleTransferFormParams,
  }));
  const [isApplyToTemplate, setIsApplyToTemplate] = useState(false);

  const applyTextPromptToTarget = useMemoizedFn((backfillTaskType: BackfillTaskType) => {
    if (openInNewTab || !backfillTextPrompt?.trim() || !keepPrompt) {
      return;
    }

    if (backfillTaskType === BackfillTaskType.IMG2VIDEO) {
      setFormParams('img2video', {
        textPrompt: backfillTextPrompt,
        isProMode: false,
        recaptionPrompt: undefined,
      });
      return;
    }

    if (backfillTaskType === BackfillTaskType.STYLE_TRANSFER) {
      updateStyleTransferFormParams('textPrompt', backfillTextPrompt);
      return;
    }

    if (
      backfillTaskType !== BackfillTaskType.CHARACTER2VIDEO &&
      backfillTaskType !== BackfillTaskType.REFERENCE2IMAGE &&
      backfillTaskType !== BackfillTaskType.STORY_GRID
    ) {
      return;
    }

    const referencePrompts = storeApi.getState()[`${backfillTaskType}FormParams`].referencePrompts || [];

    setFormParams(backfillTaskType, {
      textPrompt: backfillTextPrompt,
      textPromptWithReference: appendUnMentionedReferences(
        parseTextToTextPromptWithReference(backfillTextPrompt, referencePrompts),
        referencePrompts,
      ),
      isProMode: false,
      recaptionPrompt: undefined,
    });
  });

  useEffect(() => {
    if (supportsHover) {
      setIsNonHoverCardOpen(false);
    }
  }, [supportsHover]);

  useEffect(() => {
    if (isDirectMode) return;

    // 只有在模版工具页且不是社区灵感的情况下，才直接应用到当前模版（社区灵感可以打开新标签页应用到参考生图/参考生视频/图生视频）
    const isApplyToTemplate = /\/templates\/([^/]+)/.test(pathname) && !inspirationId;

    if (isApplyToTemplate) {
      setSelectedType(BackfillTaskType.SCENE);
    } else {
      const matchedTarget = IMAGE_BACKFILL_TARGETS.find((target) => pathname.includes(target.path));
      setSelectedType(matchedTarget?.key ?? BackfillTaskType.CHARACTER2VIDEO);
    }

    // 非社区灵感 且 在模版工具页的情况，直接应用到当前模版
    setIsApplyToTemplate(isApplyToTemplate);
  }, [pathname, isDirectMode]);

  const options: { label: string; key: BackfillTaskType }[] = useMemo(() => {
    if (isApplyToTemplate) {
      return [
        {
          label: t('create.tabScene'),
          key: BackfillTaskType.SCENE,
        },
      ];
    }

    return IMAGE_BACKFILL_TARGETS.map((target) => ({
      label: t(target.labelKey),
      key: target.key,
    }));
  }, [t, isApplyToTemplate]);

  const handleTriggerClick = useMemoizedFn(() => {
    if (!supportsHover) {
      setIsNonHoverCardOpen((prev) => !prev);
      return;
    }

    handleOptionClick();
  });

  const renderImageApplyButton = () => {
    if (content) {
      const contentElement = content as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;
      return cloneElement(contentElement, {
        onClick: (e: React.MouseEvent) => {
          if (contentElement.props.onClick) {
            contentElement.props.onClick(e);
          }
          handleTriggerClick();
        },
      });
    }

    return (
      <div
        className={cn('flex cursor-pointer items-center justify-center rounded-8', className)}
        onClick={handleTriggerClick}
      >
        <ProductLineApply1 />
      </div>
    );
  };

  const handlePushPageAndImageBackfillWithData = async (
    backfillTaskType: BackfillTaskType,
    uri: string,
    noMarkUri: string,
  ) => {
    // 优先使用无水印图片（如果存在）
    const src = noMarkUri || uri;
    try {
      const { width, height } = await getImageSize(src);

      handleBackfillImageByUrl({
        src,
        backfillTaskType,
        width,
        height,
        inspirationId: inspirationId,
        inspirationFrom: 'inspiration_apply',
        afterBackfill: ({ id }) => {
          if (showFlyTrace && container && creationId) {
            const sourceEl = container.querySelector(`#creation_${creationId}`) as HTMLElement;
            if (sourceEl) {
              setFlyPending({ flyEndId: id, sourceEl });
            }
          }
        },
      });
      applyTextPromptToTarget(backfillTaskType);
    } catch {
      toast({
        type: 'error',
        message: t('frameShotDialog.backfillFailed'),
      });
    }
  };

  // 获取灵感详情
  const { run: getInspirationDetail } = useFetch(
    () =>
      inspirationsGetInspirationDetail({
        path: {
          id: inspirationId || '',
        },
      }),
    {
      manual: true,
      onSuccess: ({
        data,
        error,
      }: {
        data?: InspirationsGetInspirationDetailResponse;
        error?: InspirationsGetInspirationDetailError;
      }) => {
        if (error) {
          toast({
            message: error.message,
            type: 'error',
          });
          return;
        }
        if (data?.task?.creations?.[0]) {
          const creation = data.task.creations[0];
          handlePushPageAndImageBackfillWithData(selectedType, creation.uri, creation.nomark_uri || '');
        }
      },
    },
  );

  const handleOptionClick = useMemoizedFn((type?: BackfillTaskType) => {
    const applyType = type ?? selectedType;
    onApplyToTool?.(BackfillTaskType2String[applyType] || '');
    if (!openInNewTab && !getBackfillAvailable(applyType)) {
      openConfirmDialog({
        title: t('frameShotDialog.replace'),
        description: t('frameShotDialog.replaceDescription'),
        onPrimaryAction: () => {
          if (isAsyncMode) {
            getInspirationDetail();
          } else if (uri) {
            handlePushPageAndImageBackfillWithData(applyType, uri, noMarkUri || '');
          }
        },
        primaryButtonText: t('frameShotDialog.confirmReplaceText'),
        secondaryButtonText: t('frameShotDialog.cancel'),
        closable: false,
      });
    } else {
      if (isAsyncMode) {
        getInspirationDetail();
      } else if (uri) {
        handlePushPageAndImageBackfillWithData(applyType, uri, noMarkUri || '');
      }
    }
  });

  if (isDirectMode) {
    return renderImageApplyButton();
  }

  return (
    <HoverCard
      openDelay={0}
      closeDelay={100}
      open={supportsHover ? undefined : isNonHoverCardOpen}
      onOpenChange={supportsHover ? undefined : setIsNonHoverCardOpen}
    >
      <HoverCardTrigger asChild>{renderImageApplyButton()}</HoverCardTrigger>
      <HoverCardPortal container={container || document.body}>
        <HoverCardContent className="w-fit bg-system-bg02 p-3 text-sm" align="center" sideOffset={2} side="top">
          <BackfillTargetOption
            selectedType={selectedType}
            options={options}
            onApply={(type) => {
              setSelectedType(type);
              handleOptionClick(type);
              if (!supportsHover) {
                setIsNonHoverCardOpen(false);
              }
            }}
            showKeepPromptToggle={!!backfillTextPrompt?.trim()}
            keepPrompt={keepPrompt}
            onKeepPromptChange={setKeepPrompt}
            titleText={titleText}
          />
          <HoverCardArrow />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
